import { useState, useEffect, useRef } from "react";

const BASE = "https://support.gs-group.fr/api";
const hdr = () => ({ "Content-Type":"application/json", Authorization:"Bearer "+localStorage.getItem("nexo_token") });

const PRIO = [
  { id:"urgent", lbl:"Urgent",  col:"#ef4444", ic:"🔴" },
  { id:"high",   lbl:"Haute",   col:"#f97316", ic:"🟠" },
  { id:"medium", lbl:"Moyenne", col:"#f59e0b", ic:"🟡" },
  { id:"low",    lbl:"Basse",   col:"#10b981", ic:"🟢" },
];
const STATUTS = [
  { id:"todo",        lbl:"À faire",  col:"#94a3b8", bg:"rgba(148,163,184,0.1)" },
  { id:"in_progress", lbl:"En cours", col:"#6366f1", bg:"rgba(99,102,241,0.1)"  },
  { id:"done",        lbl:"Terminé",  col:"#10b981", bg:"rgba(16,185,129,0.1)"  },
];
const LIST_COLORS = ["#6366f1","#f59e0b","#ef4444","#10b981","#8b5cf6","#3b82f6","#ec4899","#06b6d4"];

const fmtDate = (d) => {
  if (!d) return null;
  const dt = new Date(d); const now = new Date();
  const diff = Math.ceil((dt - now) / (1000*3600*24));
  if (diff < 0)  return { lbl: `${Math.abs(diff)}j de retard`, col:"#ef4444" };
  if (diff === 0) return { lbl: "Aujourd'hui",  col:"#f59e0b" };
  if (diff === 1) return { lbl: "Demain",        col:"#f59e0b" };
  return { lbl: dt.toLocaleDateString("fr-FR",{day:"2-digit",month:"short"}), col:"#94a3b8" };
};

const nanoid = () => Math.random().toString(36).slice(2,9);

export default function TodoView({ T, currentUser, tickets=[], interventions=[], clients=[], techniciens=[] }) {
  const [lists, setLists]         = useState([]);
  const [todos, setTodos]         = useState([]);
  const [selList, setSelList]     = useState(null); // null = toutes
  const [viewMode, setViewMode]   = useState("kanban"); // list | kanban
  const [search, setSearch]       = useState("");
  const [filterPrio, setFilterPrio] = useState(null);
  const [loading, setLoading]     = useState(true);

  const [showTask, setShowTask]   = useState(false);
  const [editTask, setEditTask]   = useState(null);
  const [showList, setShowList]   = useState(false);
  const [editList, setEditList]   = useState(null);
  const [apiError, setApiError]   = useState("");
  const [dragId, setDragId]       = useState(null);
  const [dragOver, setDragOver]   = useState(null);

  // Task form state
  const emptyTask = { titre:"", description:"", list_id:"", priorite:"medium", deadline:"", tags:"", assigned_to:"", lien_type:"", lien_id:"", subtasks:[] };
  const [taskForm, setTaskForm]   = useState(emptyTask);
  const [savingTask, setSavingTask] = useState(false);
  const [newSub, setNewSub]       = useState("");

  // List form
  const [listForm, setListForm]   = useState({ nom:"", couleur:"#6366f1" });
  const [savingList, setSavingList] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    const [ls, td] = await Promise.all([
      fetch(`${BASE}/todos/lists`, { headers: hdr() }).then(r=>r.json()).catch(()=>[]),
      fetch(`${BASE}/todos`, { headers: hdr() }).then(r=>r.json()).catch(()=>[]),
    ]);
    setLists(Array.isArray(ls)?ls:[]);
    setTodos(Array.isArray(td)?td:[]);
    setLoading(false);
  };

  const filteredTodos = todos.filter(t => {
    if (selList && Number(t.list_id) !== Number(selList)) return false;
    if (filterPrio && t.priorite !== filterPrio) return false;
    if (search) { const q=search.toLowerCase(); return t.titre.toLowerCase().includes(q)||(t.description||"").toLowerCase().includes(q); }
    return true;
  });

  // ── Task actions ─────────────────────────────────────────────────
  const openNewTask = (statut="todo") => {
    setTaskForm({ ...emptyTask, statut, list_id: selList||"" });
    setEditTask(null); setShowTask(true);
  };
  const openEditTask = (t) => {
    setTaskForm({
      titre: t.titre, description: t.description||"", list_id: t.list_id||"",
      priorite: t.priorite||"medium", deadline: t.deadline?new Date(t.deadline).toISOString().slice(0,10):"",
      tags: Array.isArray(t.tags)?t.tags.join(", "):"", assigned_to: t.assigned_to||"",
      lien_type: t.lien_type||"", lien_id: t.lien_id||"", subtasks: Array.isArray(t.subtasks)?t.subtasks:[],
      statut: t.statut||"todo",
    });
    setEditTask(t); setShowTask(true);
  };

  const saveTask = async () => {
    if (!taskForm.titre.trim()) return;
    setSavingTask(true);
    setApiError("");
    const body = {
      titre: taskForm.titre.trim(),
      description: taskForm.description||null,
      list_id: taskForm.list_id||null,
      priorite: taskForm.priorite,
      deadline: taskForm.deadline||null,
      tags: taskForm.tags ? taskForm.tags.split(",").map(s=>s.trim()).filter(Boolean) : [],
      subtasks: taskForm.subtasks||[],
      assigned_to: taskForm.assigned_to||null,
      lien_type: taskForm.lien_type||null,
      lien_id: taskForm.lien_id||null,
      statut: taskForm.statut||"todo",
    };
    try {
      if (editTask) {
        const r = await fetch(`${BASE}/todos/${editTask.id}`, { method:"PATCH", headers:hdr(), body:JSON.stringify(body) });
        const res = await r.json();
        if (!r.ok) { setApiError(res.error||"Erreur serveur"); setSavingTask(false); return; }
        setTodos(p => p.map(t => t.id===editTask.id ? { ...t, ...res } : t));
      } else {
        const r = await fetch(`${BASE}/todos`, { method:"POST", headers:hdr(), body:JSON.stringify(body) });
        const res = await r.json();
        if (!r.ok) { setApiError(res.error||"Erreur serveur"); setSavingTask(false); return; }
        setTodos(p => [res, ...p]);
      }
      setSavingTask(false);
      setShowTask(false);
    } catch(e) {
      setApiError("Erreur réseau : "+e.message);
      setSavingTask(false);
    }
  };

  const deleteTask = async (id) => {
    await fetch(`${BASE}/todos/${id}`, { method:"DELETE", headers:hdr() });
    setTodos(p => p.filter(t => t.id !== id));
  };

  const toggleSubtask = async (task, subId) => {
    const subtasks = (task.subtasks||[]).map(s => s.id===subId ? { ...s, done:!s.done } : s);
    setTodos(p => p.map(t => t.id===task.id ? { ...t, subtasks } : t));
    await fetch(`${BASE}/todos/${task.id}`, { method:"PATCH", headers:hdr(), body:JSON.stringify({ subtasks }) });
  };

  const moveTask = async (id, statut) => {
    setTodos(p => p.map(t => t.id===id ? { ...t, statut } : t));
    await fetch(`${BASE}/todos/${id}`, { method:"PATCH", headers:hdr(), body:JSON.stringify({ statut }) });
  };

  const toggleDone = async (task) => {
    const statut = task.statut==="done" ? "todo" : "done";
    setTodos(p => p.map(t => t.id===task.id ? { ...t, statut } : t));
    await fetch(`${BASE}/todos/${task.id}`, { method:"PATCH", headers:hdr(), body:JSON.stringify({ statut }) });
  };

  // ── List actions ──────────────────────────────────────────────────
  const saveList = async () => {
    if (!listForm.nom.trim()) return;
    setSavingList(true);
    setApiError("");
    try {
      if (editList) {
        const r = await fetch(`${BASE}/todos/lists/${editList.id}`, { method:"PATCH", headers:hdr(), body:JSON.stringify(listForm) });
        const res = await r.json();
        if (!r.ok) { setApiError(res.error||"Erreur serveur"); setSavingList(false); return; }
        setLists(p => p.map(l => l.id===editList.id ? { ...l, ...res } : l));
      } else {
        const r = await fetch(`${BASE}/todos/lists`, { method:"POST", headers:hdr(), body:JSON.stringify(listForm) });
        const res = await r.json();
        if (!r.ok) { setApiError(res.error||"Erreur serveur"); setSavingList(false); return; }
        setLists(p => [...p, res]);
        setSelList(res.id);
      }
      setSavingList(false);
      setShowList(false);
    } catch(e) {
      setApiError("Erreur réseau : "+e.message);
      setSavingList(false);
    }
  };

  const deleteList = async (id) => {
    await fetch(`${BASE}/todos/lists/${id}`, { method:"DELETE", headers:hdr() });
    setLists(p => p.filter(l => l.id!==id));
    setTodos(p => p.filter(t => t.list_id!==id));
    if (selList===id) setSelList(null);
  };

  // ── Drag & drop (Kanban) ──────────────────────────────────────────
  const handleDragStart = (id) => setDragId(id);
  const handleDragOver = (e, col) => { e.preventDefault(); setDragOver(col); };
  const handleDrop = (col) => { if (dragId) moveTask(dragId, col); setDragId(null); setDragOver(null); };
  const handleDragEnd = () => { setDragId(null); setDragOver(null); };

  // ── Stats ─────────────────────────────────────────────────────────
  const totalActive = todos.filter(t=>t.statut!=="done").length;
  const todayDeadlines = todos.filter(t=>{ if(!t.deadline||t.statut==="done") return false; const d=new Date(t.deadline); const n=new Date(); return d.toDateString()===n.toDateString(); });
  const overdue = todos.filter(t=>{ if(!t.deadline||t.statut==="done") return false; return new Date(t.deadline)<new Date(); });

  // ── Task Card ─────────────────────────────────────────────────────
  const TaskCard = ({ task, compact=false }) => {
    const pr = PRIO.find(p=>p.id===task.priorite)||PRIO[2];
    const dl = fmtDate(task.deadline);
    const doneCount = (task.subtasks||[]).filter(s=>s.done).length;
    const totalSub  = (task.subtasks||[]).length;
    const lienLabel = task.lien_type==="ticket" ? tickets.find(t=>t.id===task.lien_id)
      : task.lien_type==="client" ? clients.find(c=>c.id===task.lien_id)
      : task.lien_type==="intervention" ? interventions.find(i=>i.id===task.lien_id) : null;
    const assignedColor = task.assigned_color || "#6366f1";
    const list = lists.find(l => l.id===task.list_id);

    return (
      <div
        className="task-card"
        draggable
        onDragStart={() => handleDragStart(task.id)}
        onDragEnd={handleDragEnd}
        style={{ background:T.card, border:`1px solid ${task.statut==="done"?T.border+"60":T.border}`, borderRadius:13, padding:"13px 14px", cursor:"grab", transition:"all 0.15s", opacity:task.statut==="done"?0.65:1, position:"relative" }}
        onMouseEnter={e=>{ e.currentTarget.style.borderColor=pr.col; e.currentTarget.style.transform="translateY(-1px)"; e.currentTarget.style.boxShadow=`0 4px 16px ${pr.col}18`; }}
        onMouseLeave={e=>{ e.currentTarget.style.borderColor=T.border; e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="none"; }}>

        {/* Top row */}
        <div style={{ display:"flex", alignItems:"flex-start", gap:8, marginBottom:8 }}>
          {/* Done checkbox */}
          <button onClick={()=>toggleDone(task)} style={{ width:18,height:18,borderRadius:5,border:`2px solid ${task.statut==="done"?"#10b981":T.border}`,background:task.statut==="done"?"#10b981":"transparent",flexShrink:0,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",marginTop:2 }}>
            {task.statut==="done"&&<svg width="10" height="10" viewBox="0 0 10 10"><polyline points="1.5,5 4,7.5 8.5,2.5" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round"/></svg>}
          </button>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:600, color:task.statut==="done"?T.muted:T.text, textDecoration:task.statut==="done"?"line-through":"none", lineHeight:1.35, overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>{task.titre}</div>
          </div>
          <button onClick={()=>openEditTask(task)} style={{ background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:13,flexShrink:0,padding:2,opacity:0 }} className="task-edit-btn">✏️</button>
        </div>

        {/* Tags */}
        {Array.isArray(task.tags)&&task.tags.length>0&&(
          <div style={{ display:"flex",gap:4,flexWrap:"wrap",marginBottom:7 }}>
            {task.tags.map(tag=>(
              <span key={tag} style={{ fontSize:10,fontWeight:600,color:T.accent,background:T.accentDim,padding:"1px 7px",borderRadius:99 }}>{tag}</span>
            ))}
          </div>
        )}

        {/* Subtasks progress */}
        {totalSub>0&&(
          <div style={{ marginBottom:7 }}>
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:3,fontSize:10,color:T.muted }}>
              <span>Sous-tâches</span><span style={{ fontWeight:700 }}>{doneCount}/{totalSub}</span>
            </div>
            <div style={{ height:4,background:T.border,borderRadius:99,overflow:"hidden" }}>
              <div style={{ height:"100%",width:`${totalSub>0?doneCount/totalSub*100:0}%`,background:"#10b981",borderRadius:99,transition:"width 0.3s" }}/>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginTop:6 }}>
          {/* Priority */}
          <span style={{ fontSize:10,fontWeight:700,color:pr.col,background:`${pr.col}18`,padding:"2px 7px",borderRadius:99 }}>{pr.ic} {pr.lbl}</span>
          {/* Deadline */}
          {dl&&<span style={{ fontSize:10,fontWeight:700,color:dl.col,background:`${dl.col}15`,padding:"2px 7px",borderRadius:99 }}>📅 {dl.lbl}</span>}
          {/* List badge */}
          {list&&<span style={{ fontSize:10,color:"#fff",background:list.couleur,padding:"2px 8px",borderRadius:99,fontWeight:700 }}>{list.nom}</span>}
          {/* Assigned avatar */}
          {task.assigned_to&&(
            <div style={{ marginLeft:"auto",width:22,height:22,borderRadius:"50%",background:`${assignedColor}30`,border:`1.5px solid ${assignedColor}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:800,color:assignedColor }}>
              {((task.assigned_prenom||"?")[0]+(task.assigned_nom||"?")[0]).toUpperCase()}
            </div>
          )}
          {/* CRM link */}
          {lienLabel&&<span style={{ fontSize:9,color:T.muted,marginLeft:"auto",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:80 }}>🔗 {lienLabel.titre||lienLabel.nom||lienLabel.raison_sociale||"#"+task.lien_id}</span>}
          {/* Delete */}
          <button onClick={(e)=>{e.stopPropagation();if(window.confirm("Supprimer cette tâche ?"))deleteTask(task.id);}} style={{ marginLeft:"auto",background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:11,padding:"1px 4px",opacity:0.5 }} onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=0.5}>🗑</button>
        </div>
      </div>
    );
  };

  // Lien options
  const lienItems = taskForm.lien_type==="ticket" ? tickets.map(t=>({id:t.id,lbl:`SAV #${t.id} — ${t.titre||t.sujet}`}))
    : taskForm.lien_type==="client" ? clients.map(c=>({id:c.id,lbl:c.nom||c.raison_sociale}))
    : taskForm.lien_type==="intervention" ? interventions.slice(0,50).map(i=>({id:i.id,lbl:`#${i.id} ${i.titre}`}))
    : [];

  if (loading) return <div style={{ textAlign:"center",padding:60,color:T.muted }}>⏳ Chargement...</div>;

  return (
    <div style={{ display:"flex",flexDirection:"column",gap:0,height:"calc(100vh - 100px)",overflow:"hidden" }}>
      <style>{`
        .task-card:hover .task-edit-btn{opacity:1!important}
        .list-item:hover .list-menu-btn{opacity:1!important}
        @keyframes slide-up{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fade-in{from{opacity:0}to{opacity:1}}
      `}</style>

      {/* ── Top bar ─────────────────────────────────────────────── */}
      <div style={{ display:"flex",alignItems:"center",gap:12,padding:"0 0 16px",flexWrap:"wrap",flexShrink:0 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:24,fontWeight:900,color:T.text,letterSpacing:"-0.5px" }}>✅ Mes Tâches</div>
          <div style={{ fontSize:12,color:T.muted,marginTop:3,display:"flex",gap:12 }}>
            <span style={{ color:T.accent,fontWeight:600 }}>{totalActive} actives</span>
            {overdue.length>0&&<span style={{ color:"#ef4444",fontWeight:700 }}>⚠️ {overdue.length} en retard</span>}
            {todayDeadlines.length>0&&<span style={{ color:"#f59e0b",fontWeight:700 }}>📅 {todayDeadlines.length} aujourd'hui</span>}
          </div>
        </div>
        {/* View toggle */}
        <div style={{ display:"flex",background:T.card,border:`1px solid ${T.border}`,borderRadius:10,overflow:"hidden" }}>
          {[["list","☰ Liste"],["kanban","⊞ Kanban"]].map(([id,lbl])=>(
            <button key={id} onClick={()=>setViewMode(id)}
              style={{ padding:"8px 14px",background:viewMode===id?T.accentDim:"transparent",border:"none",color:viewMode===id?T.accent:T.muted,fontSize:12,fontWeight:viewMode===id?700:500,cursor:"pointer" }}>
              {lbl}
            </button>
          ))}
        </div>
        {/* Priority filter */}
        <div style={{ display:"flex",gap:5 }}>
          {PRIO.map(p=>(
            <button key={p.id} onClick={()=>setFilterPrio(filterPrio===p.id?null:p.id)}
              style={{ padding:"6px 10px",borderRadius:8,border:`1.5px solid ${filterPrio===p.id?p.col:T.border}`,background:filterPrio===p.id?`${p.col}18`:"transparent",color:filterPrio===p.id?p.col:T.muted,fontSize:11,cursor:"pointer" }}>
              {p.ic}
            </button>
          ))}
        </div>
        <button onClick={()=>openNewTask()} style={{ padding:"9px 18px",borderRadius:10,background:T.accent,border:"none",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6 }}>＋ Tâche</button>
      </div>

      {/* ── Main layout ─────────────────────────────────────────── */}
      <div style={{ display:"flex",gap:16,flex:1,overflow:"hidden" }}>

        {/* Sidebar lists */}
        <div style={{ width:230,flexShrink:0,display:"flex",flexDirection:"column",gap:4,overflowY:"auto",paddingRight:4 }}>
          {/* All tasks */}
          <button onClick={()=>setSelList(null)}
            style={{ display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:9,border:`1px solid ${selList===null?T.accent:T.border}`,background:selList===null?T.accentDim:"transparent",color:selList===null?T.accent:T.text,fontSize:13,fontWeight:selList===null?700:500,cursor:"pointer",transition:"all 0.12s" }}>
            <span style={{ fontSize:16 }}>📋</span>
            <span style={{ flex:1,textAlign:"left" }}>Toutes les tâches</span>
            <span style={{ fontSize:11,color:T.muted,background:T.bg,borderRadius:99,padding:"1px 7px",fontWeight:700 }}>{todos.filter(t=>t.statut!=="done").length}</span>
          </button>

          <div style={{ fontSize:9,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:"1.2px",padding:"8px 12px 4px" }}>Mes listes</div>

          {lists.map(list=>(
            <div key={list.id} className="list-item" style={{ position:"relative",display:"flex",alignItems:"center",gap:4 }}>
              <button onClick={()=>setSelList(list.id)}
                style={{ display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:9,border:`1px solid ${selList===list.id?list.couleur:T.border}`,background:selList===list.id?`${list.couleur}14`:"transparent",color:selList===list.id?list.couleur:T.text,fontSize:13,fontWeight:selList===list.id?700:500,cursor:"pointer",flex:1,transition:"all 0.12s" }}>
                <div style={{ width:10,height:10,borderRadius:"50%",background:list.couleur,flexShrink:0 }}/>
                <span style={{ flex:1,textAlign:"left",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{list.nom}</span>
                <span style={{ fontSize:11,color:T.muted,fontWeight:700,flexShrink:0 }}>{list.nb_actifs||0}</span>
              </button>
              <button className="list-menu-btn" onClick={()=>{ setListForm({nom:list.nom,couleur:list.couleur}); setEditList(list); setShowList(true); }}
                style={{ background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:14,padding:"4px 6px",borderRadius:7,opacity:0,flexShrink:0 }}
                title="Modifier / Supprimer">⋯</button>
            </div>
          ))}

          <button onClick={()=>{ setListForm({nom:"",couleur:LIST_COLORS[lists.length%LIST_COLORS.length]}); setEditList(null); setShowList(true); }}
            style={{ display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:9,border:`1px dashed ${T.border}`,background:"transparent",color:T.muted,fontSize:12,cursor:"pointer",marginTop:4 }}>
            ＋ Créer une liste
          </button>

          {/* Assigné à moi widget */}
          {(() => {
            const myId = String(currentUser?.id || "");
            const assigned = todos.filter(t => t.statut !== "done" && String(t.assigned_to) === myId);
            return (
              <div style={{ marginTop:16,background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:"12px",overflow:"hidden" }}>
                <div style={{ fontSize:10,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:"1px",marginBottom:8 }}>Assigné à moi</div>
                {assigned.length === 0
                  ? <div style={{ fontSize:11,color:T.muted }}>Aucune tâche assignée</div>
                  : assigned.slice(0,5).map(t=>(
                    <div key={t.id} onClick={()=>openEditTask(t)} style={{ display:"flex",alignItems:"center",gap:7,padding:"6px 0",borderBottom:`1px solid ${T.border}20`,cursor:"pointer" }}>
                      <div style={{ width:6,height:6,borderRadius:"50%",background:PRIO.find(p=>p.id===t.priorite)?.col||T.muted,flexShrink:0 }}/>
                      <span style={{ fontSize:11,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1 }}>{t.titre}</span>
                    </div>
                  ))
                }
              </div>
            );
          })()}

        </div>

        {/* Main area */}
        <div style={{ flex:1,overflow:"auto" }}>
          {/* ── LIST VIEW ─────────────────────────────────────────── */}
          {viewMode==="list" && (
            <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
              {/* Group by status */}
              {STATUTS.map(st=>{
                const items = filteredTodos.filter(t=>t.statut===st.id);
                return (
                  <div key={st.id} style={{ background:T.card,border:`1px solid ${T.border}`,borderRadius:14,overflow:"hidden" }}>
                    <div style={{ padding:"12px 18px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:10,cursor:"pointer" }}>
                      <span style={{ width:10,height:10,borderRadius:"50%",background:st.col,display:"inline-block",flexShrink:0 }}/>
                      <span style={{ fontSize:13,fontWeight:700,color:st.col,flex:1 }}>{st.lbl}</span>
                      <span style={{ fontSize:12,fontWeight:700,color:st.col,background:st.bg,padding:"2px 9px",borderRadius:99 }}>{items.length}</span>
                      <button onClick={()=>openNewTask(st.id)} style={{ padding:"4px 10px",borderRadius:7,background:T.bg,border:`1px solid ${T.border}`,color:T.muted,fontSize:11,cursor:"pointer" }}>＋</button>
                    </div>
                    {items.length===0
                      ? <div style={{ padding:"20px 18px",color:T.muted,fontSize:12,textAlign:"center" }}>Aucune tâche</div>
                      : items.map(t=>(
                          <div key={t.id} style={{ padding:"0 12px 12px",marginTop:12 }}>
                            <TaskCard task={t}/>
                          </div>
                        ))
                    }
                  </div>
                );
              })}
              {filteredTodos.length===0&&(
                <div style={{ textAlign:"center",padding:"60px 20px",color:T.muted }}>
                  <div style={{ fontSize:48,marginBottom:16 }}>✅</div>
                  <div style={{ fontSize:16,fontWeight:700,color:T.text,marginBottom:8 }}>Aucune tâche</div>
                  <button onClick={()=>openNewTask()} style={{ padding:"10px 20px",borderRadius:10,background:T.accent,border:"none",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer" }}>Créer une tâche</button>
                </div>
              )}
            </div>
          )}

          {/* ── KANBAN VIEW ───────────────────────────────────────── */}
          {viewMode==="kanban" && (
            <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,height:"100%",alignItems:"start" }}>
              {STATUTS.map(st=>{
                const items = filteredTodos.filter(t=>t.statut===st.id);
                const isOver = dragOver===st.id;
                return (
                  <div key={st.id}
                    onDragOver={e=>handleDragOver(e,st.id)}
                    onDrop={()=>handleDrop(st.id)}
                    style={{ background:isOver?`${st.col}08`:T.bg, border:`2px dashed ${isOver?st.col:T.border}`, borderRadius:14, overflow:"hidden", transition:"all 0.15s", minHeight:200 }}>
                    {/* Column header */}
                    <div style={{ padding:"13px 16px",background:`${st.col}10`,borderBottom:`2px solid ${st.col}30`,display:"flex",alignItems:"center",justifyContent:"space-between" }}>
                      <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                        <div style={{ width:9,height:9,borderRadius:"50%",background:st.col }}/>
                        <span style={{ fontSize:13,fontWeight:700,color:st.col }}>{st.lbl}</span>
                      </div>
                      <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                        <span style={{ fontSize:12,fontWeight:800,color:st.col,background:`${st.col}20`,padding:"2px 9px",borderRadius:99 }}>{items.length}</span>
                        <button onClick={()=>openNewTask(st.id)} style={{ width:24,height:24,borderRadius:7,background:`${st.col}20`,border:"none",color:st.col,fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>＋</button>
                      </div>
                    </div>
                    {/* Cards */}
                    <div style={{ padding:10,display:"flex",flexDirection:"column",gap:9,maxHeight:"70vh",overflowY:"auto" }}>
                      {items.length===0&&!isOver&&(
                        <div style={{ textAlign:"center",padding:"24px 12px",color:T.muted,fontSize:12 }}>Glisser ici</div>
                      )}
                      {items.map(t=><TaskCard key={t.id} task={t}/>)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── MODAL TÂCHE ─────────────────────────────────────────── */}
      {showTask&&(
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",padding:20,animation:"fade-in 0.15s" }} onClick={e=>{if(e.target===e.currentTarget)setShowTask(false);}}>
          <div style={{ background:T.card,borderRadius:18,width:"100%",maxWidth:620,maxHeight:"92vh",overflow:"auto",boxShadow:"0 24px 64px rgba(0,0,0,0.3)",animation:"slide-up 0.2s" }}>
            {/* Modal header */}
            <div style={{ padding:"18px 22px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between" }}>
              <div style={{ fontSize:16,fontWeight:800,color:T.text }}>{editTask?"✏️ Modifier la tâche":"＋ Nouvelle tâche"}</div>
              <button onClick={()=>setShowTask(false)} style={{ background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:20 }}>✕</button>
            </div>
            <div style={{ padding:"20px 22px",display:"flex",flexDirection:"column",gap:18 }}>

              {/* Titre */}
              <div>
                <label style={{ fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:6 }}>TITRE *</label>
                <input value={taskForm.titre} onChange={e=>setTaskForm(p=>({...p,titre:e.target.value}))} placeholder="Titre de la tâche..."
                  style={{ background:T.bg,border:`1.5px solid ${taskForm.titre?T.accent:T.border}`,borderRadius:10,padding:"11px 14px",color:T.text,fontSize:15,fontWeight:600,outline:"none",width:"100%" }} autoFocus/>
              </div>

              {/* Description */}
              <div>
                <label style={{ fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:6 }}>DESCRIPTION</label>
                <textarea value={taskForm.description} onChange={e=>setTaskForm(p=>({...p,description:e.target.value}))} rows={3} placeholder="Détails, contexte..."
                  style={{ background:T.bg,border:`1px solid ${T.border}`,borderRadius:10,padding:"11px 14px",color:T.text,fontSize:13,outline:"none",resize:"vertical",lineHeight:1.6,width:"100%" }}/>
              </div>

              {/* Row: Liste + Statut */}
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14 }}>
                <div>
                  <label style={{ fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:6 }}>LISTE</label>
                  <select value={taskForm.list_id} onChange={e=>setTaskForm(p=>({...p,list_id:e.target.value||null}))}
                    style={{ background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none",width:"100%",cursor:"pointer" }}>
                    <option value="">— Aucune liste —</option>
                    {lists.map(l=><option key={l.id} value={l.id}>{l.nom}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:6 }}>STATUT</label>
                  <select value={taskForm.statut||"todo"} onChange={e=>setTaskForm(p=>({...p,statut:e.target.value}))}
                    style={{ background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none",width:"100%",cursor:"pointer" }}>
                    {STATUTS.map(s=><option key={s.id} value={s.id}>{s.lbl}</option>)}
                  </select>
                </div>
              </div>

              {/* Priorité */}
              <div>
                <label style={{ fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:8 }}>PRIORITÉ</label>
                <div style={{ display:"flex",gap:8 }}>
                  {PRIO.map(p=>(
                    <button key={p.id} onClick={()=>setTaskForm(f=>({...f,priorite:p.id}))}
                      style={{ flex:1,padding:"8px",borderRadius:9,border:`2px solid ${taskForm.priorite===p.id?p.col:T.border}`,background:taskForm.priorite===p.id?`${p.col}18`:"transparent",color:taskForm.priorite===p.id?p.col:T.muted,fontSize:12,fontWeight:taskForm.priorite===p.id?700:500,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:5 }}>
                      {p.ic} {p.lbl}
                    </button>
                  ))}
                </div>
              </div>

              {/* Row: Deadline + Assigné */}
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14 }}>
                <div>
                  <label style={{ fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:6 }}>📅 ÉCHÉANCE</label>
                  <input type="date" value={taskForm.deadline} onChange={e=>setTaskForm(p=>({...p,deadline:e.target.value}))}
                    style={{ background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none",width:"100%" }}/>
                </div>
                <div>
                  <label style={{ fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:6 }}>👤 ASSIGNER À</label>
                  <select value={taskForm.assigned_to} onChange={e=>setTaskForm(p=>({...p,assigned_to:e.target.value||null}))}
                    style={{ background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none",width:"100%",cursor:"pointer" }}>
                    <option value="">— Non assigné —</option>
                    {techniciens.map(t=><option key={t.id} value={t.id}>{t.prenom} {t.nom}</option>)}
                  </select>
                </div>
              </div>

              {/* Tags */}
              <div>
                <label style={{ fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:6 }}>🏷️ TAGS (séparés par virgule)</label>
                <input value={taskForm.tags} onChange={e=>setTaskForm(p=>({...p,tags:e.target.value}))} placeholder="bug, urgent, client-X..."
                  style={{ background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none",width:"100%" }}/>
              </div>

              {/* Lien CRM */}
              <div>
                <label style={{ fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:8 }}>🔗 LIEN CRM</label>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 2fr",gap:10 }}>
                  <select value={taskForm.lien_type} onChange={e=>setTaskForm(p=>({...p,lien_type:e.target.value,lien_id:""}))}
                    style={{ background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none",cursor:"pointer" }}>
                    <option value="">— Aucun —</option>
                    <option value="ticket">🎫 SAV / Ticket</option>
                    <option value="client">👤 Client</option>
                    <option value="intervention">🔧 Intervention</option>
                  </select>
                  {taskForm.lien_type&&(
                    <select value={taskForm.lien_id} onChange={e=>setTaskForm(p=>({...p,lien_id:e.target.value||null}))}
                      style={{ background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"9px 12px",color:T.text,fontSize:13,outline:"none",cursor:"pointer" }}>
                      <option value="">— Sélectionner —</option>
                      {lienItems.map(i=><option key={i.id} value={i.id}>{i.lbl}</option>)}
                    </select>
                  )}
                </div>
              </div>

              {/* Sous-tâches */}
              <div>
                <label style={{ fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:8 }}>☑️ SOUS-TÂCHES ({(taskForm.subtasks||[]).filter(s=>s.done).length}/{(taskForm.subtasks||[]).length})</label>
                <div style={{ display:"flex",flexDirection:"column",gap:6,marginBottom:8 }}>
                  {(taskForm.subtasks||[]).map(s=>(
                    <div key={s.id} style={{ display:"flex",alignItems:"center",gap:8,padding:"6px 10px",background:T.bg,borderRadius:8,border:`1px solid ${T.border}` }}>
                      <button onClick={()=>setTaskForm(p=>({...p,subtasks:p.subtasks.map(x=>x.id===s.id?{...x,done:!x.done}:x)}))}
                        style={{ width:16,height:16,borderRadius:4,border:`2px solid ${s.done?"#10b981":T.border}`,background:s.done?"#10b981":"transparent",flexShrink:0,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>
                        {s.done&&<svg width="9" height="9" viewBox="0 0 9 9"><polyline points="1,4.5 3.5,7 8,2" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round"/></svg>}
                      </button>
                      <span style={{ flex:1,fontSize:12,color:s.done?T.muted:T.text,textDecoration:s.done?"line-through":"none" }}>{s.titre}</span>
                      <button onClick={()=>setTaskForm(p=>({...p,subtasks:p.subtasks.filter(x=>x.id!==s.id)}))} style={{ background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:12 }}>✕</button>
                    </div>
                  ))}
                </div>
                <div style={{ display:"flex",gap:8 }}>
                  <input value={newSub} onChange={e=>setNewSub(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&newSub.trim()){setTaskForm(p=>({...p,subtasks:[...(p.subtasks||[]),{id:nanoid(),titre:newSub.trim(),done:false}]}));setNewSub("");}}} placeholder="Ajouter une sous-tâche..."
                    style={{ flex:1,background:T.bg,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 12px",color:T.text,fontSize:12,outline:"none" }}/>
                  <button onClick={()=>{if(newSub.trim()){setTaskForm(p=>({...p,subtasks:[...(p.subtasks||[]),{id:nanoid(),titre:newSub.trim(),done:false}]}));setNewSub("");}}} style={{ padding:"8px 14px",borderRadius:8,background:T.accentDim,border:`1px solid ${T.accent}`,color:T.accent,fontSize:12,fontWeight:700,cursor:"pointer" }}>＋</button>
                </div>
              </div>
            </div>

            {/* Erreur API */}
            {apiError&&<div style={{ margin:"0 22px 12px",padding:"10px 14px",background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:9,fontSize:12,color:"#ef4444",fontWeight:600 }}>⚠️ {apiError}</div>}

            {/* Modal footer */}
            <div style={{ padding:"16px 22px",borderTop:`1px solid ${T.border}`,display:"flex",gap:10 }}>
              <button onClick={()=>{setShowTask(false);setApiError("");}} style={{ flex:1,padding:"11px",borderRadius:10,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,fontSize:13,fontWeight:600,cursor:"pointer" }}>Annuler</button>
              {editTask&&<button onClick={()=>{deleteTask(editTask.id);setShowTask(false);}} style={{ padding:"11px 18px",borderRadius:10,border:"1px solid rgba(239,68,68,0.3)",background:"rgba(239,68,68,0.08)",color:"#ef4444",fontSize:13,fontWeight:600,cursor:"pointer" }}>🗑️</button>}
              <button onClick={saveTask} disabled={savingTask||!taskForm.titre.trim()} style={{ flex:2,padding:"11px",borderRadius:10,background:`linear-gradient(135deg,${T.accent},#8b5cf6)`,border:"none",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",opacity:(!taskForm.titre.trim())?0.5:1 }}>
                {savingTask?"Sauvegarde...":editTask?"💾 Modifier":"✅ Créer la tâche"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL LISTE ─────────────────────────────────────────── */}
      {showList&&(
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",padding:20 }} onClick={e=>{if(e.target===e.currentTarget)setShowList(false);}}>
          <div style={{ background:T.card,borderRadius:16,width:"100%",maxWidth:400,boxShadow:"0 16px 48px rgba(0,0,0,0.3)" }}>
            <div style={{ padding:"18px 22px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between" }}>
              <div style={{ fontSize:16,fontWeight:800,color:T.text }}>{editList?"Modifier la liste":"Nouvelle liste"}</div>
              <button onClick={()=>setShowList(false)} style={{ background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:18 }}>✕</button>
            </div>
            <div style={{ padding:"20px 22px",display:"flex",flexDirection:"column",gap:16 }}>
              <div>
                <label style={{ fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:6 }}>NOM *</label>
                <input value={listForm.nom} onChange={e=>setListForm(p=>({...p,nom:e.target.value}))} placeholder="Nom de la liste..." autoFocus
                  style={{ background:T.bg,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 14px",color:T.text,fontSize:14,fontWeight:600,outline:"none",width:"100%" }}/>
              </div>
              <div>
                <label style={{ fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:8 }}>COULEUR</label>
                <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
                  {LIST_COLORS.map(c=>(
                    <button key={c} onClick={()=>setListForm(p=>({...p,couleur:c}))}
                      style={{ width:32,height:32,borderRadius:"50%",background:c,border:listForm.couleur===c?`3px solid ${T.text}`:"3px solid transparent",cursor:"pointer",transition:"all 0.15s" }}/>
                  ))}
                </div>
              </div>
              {editList&&(
                <button onClick={()=>{if(window.confirm("Supprimer cette liste et ses tâches ?"))deleteList(editList.id);setShowList(false);}} style={{ padding:"9px",borderRadius:9,border:"1px solid rgba(239,68,68,0.3)",background:"rgba(239,68,68,0.08)",color:"#ef4444",fontSize:12,fontWeight:600,cursor:"pointer" }}>
                  🗑️ Supprimer la liste
                </button>
              )}
            </div>
            {apiError&&<div style={{ margin:"0 22px 10px",padding:"10px 14px",background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:9,fontSize:12,color:"#ef4444",fontWeight:600 }}>⚠️ {apiError}</div>}
            <div style={{ padding:"14px 22px",borderTop:`1px solid ${T.border}`,display:"flex",gap:10 }}>
              <button onClick={()=>{setShowList(false);setApiError("");}} style={{ flex:1,padding:"10px",borderRadius:9,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,fontSize:13,cursor:"pointer" }}>Annuler</button>
              <button onClick={saveList} disabled={savingList||!listForm.nom.trim()} style={{ flex:2,padding:"10px",borderRadius:9,background:listForm.couleur,border:"none",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",opacity:!listForm.nom.trim()?0.5:1 }}>
                {savingList?"Sauvegarde...":editList?"Modifier":"Créer la liste"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
